import { useEffect, useState } from "react";
import { ScrollableCarousels, type CarouselType } from "../ScrollableCarousels";
import type { BlogSchema } from "./blog.types";
import { cn } from "@/lib/utils";

interface ClientBlogsSectionProps {
  title: string;
  city: string;
  country: string;
  className?: string;
}

type BlogType = Pick<
  BlogSchema,
  "title" | "slug" | "unsplashImageData" | "createdAt"
>;

const BlogSkeleton = () => (
  <div>
    <div className="flex justify-between items-end px-4 md:pl-2 md:pr-0">
      <div className="text-3xl md:text-4xl font-extralight tracking-[-0.01em] line-clamp-1 capitalize leading-[2.5] md:leading-loose px-0">
        <span className="inline-block h-[1em] w-48 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
      </div>
      <div className="text-[13px] md:text-sm font-medium leading-[3.7] md:leading-[3.4] md:px-1.5 relative bottom-1.5 sm:bottom-0">
        <span className="inline-block h-[1em] w-20 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
      </div>
    </div>
    <div className="relative">
      <div className="-ml-1 px-3.5 md:pl-0 flex gap-2.5 overflow-x-scroll scrollbar-hidden">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="min-w-[52vw] md:min-w-fit md:h-[28rem] aspect-[2/3] md:aspect-[3/4] bg-neutral-200 dark:bg-neutral-800 rounded-lg animate-pulse relative overflow-hidden"
          >
            {/* Skeleton for the gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/30" />
            {/* Skeleton for the text content */}
            <div className="absolute inset-x-0 bottom-0 px-2 py-2 md:px-4 md:py-3 flex items-center z-10 gap-1 md:gap-[5px]">
              <div className="relative w-full">
                <div className="h-3 md:h-4 w-24 bg-white/20 rounded mb-1 animate-pulse" />
                <div className="h-4 md:h-6 w-3/4 bg-white/30 rounded animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export function ClientBlogsSection({
  title,
  city,
  country,
  className,
}: ClientBlogsSectionProps) {
  const [blogs, setBlogs] = useState<BlogType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(
          `/api/blogs/city.json?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch blogs: ${response.statusText}`);
        }

        const data = await response.json();
        setBlogs(data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch blogs");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchBlogs();
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [city, country]);

  if (loading) {
    return (
      <section
        className={cn(
          "mx-auto max-w-screen-xl md:px-4 mt-8 md:mt-40 xl:mt-48",
          className,
        )}
      >
        <BlogSkeleton />
      </section>
    );
  }

  if (error || blogs.length === 0) {
    console.error("Blog fetch error:", error);
    return null; // Fail silently - blogs are not critical to page functionality
  }

  const carousels: CarouselType[] = [
    {
      category: {
        name: title,
      },
      blogs: blogs,
    },
  ];

  return (
    <section
      className={cn(
        "mx-auto max-w-screen-xl md:px-4 mt-8 md:mt-40 xl:mt-48",
        className,
      )}
    >
      <ScrollableCarousels carousels={carousels} />
    </section>
  );
}
